import type { SvgIconComponent } from "@mui/icons-material";
import { Badge, Box, ButtonBase, Divider, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import React, { useEffect, useId, useLayoutEffect, useRef } from "react";

import CustomScrollbars from "components/CustomScrollbars";
import { PRODUCT_ICONS } from "components/common/navIcons";
import {
  POINTER_COARSE_MEDIA,
  SHELL_NAV_ROW_HEIGHT_2K_PX,
  SHELL_NAV_ROW_HEIGHT_4K_PX,
  SHELL_NAV_ROW_HEIGHT_PX,
  SHELL_NAV_ROW_HEIGHT_SHORT_PX,
  SHELL_NAV_ROW_INNER_SPACING,
  SHELL_NAV_ROW_MIN_TOUCH_PX,
  SHELL_SIDEBAR_WIDTH_2K_PX,
  SHELL_SIDEBAR_WIDTH_4K_PX,
  SHELL_SIDEBAR_WIDTH_PX,
  VIEWPORT_2K_MIN_MEDIA,
  VIEWPORT_4K_MIN_MEDIA,
  VIEWPORT_SHORT_MAX_MEDIA,
} from "config";
import { activeNavKey, NavCatalog, NavProduct } from "utils/navModel";

/**
 * The sidebar of both shells: Home, then the catalog's sections, each a list
 * labelled by its header. Rows are links, so a new tab works; a plain click goes
 * through `onNavigate`, where the shell runs its unsaved-changes guards.
 */

// The header remounts with every page, and the list would jump back to the
// top; the last position of each sidebar outlives it here.
const scrollTops: Record<string, number> = {};

const iconSx = {
  fontSize: 22,
  color: "inherit",
  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 24 },
  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 26 },
};

const iconSlotSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  flexShrink: 0,
  lineHeight: 0,
  [VIEWPORT_2K_MIN_MEDIA]: { width: 26, height: 26 },
  [VIEWPORT_4K_MIN_MEDIA]: { width: 28, height: 28 },
};

const listSx = { listStyle: "none", m: 0, p: 0 };

// Media keys keep their order: a 2560x800 window is both 2K and short, and a
// touch screen never gets a row below a fingertip.
function rowSx(theme: Theme, active: boolean) {
  const nav = theme.palette.calibrate;
  return {
    position: "relative" as const,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.5,
    width: "100%",
    height: SHELL_NAV_ROW_HEIGHT_PX,
    px: SHELL_NAV_ROW_INNER_SPACING,
    boxSizing: "border-box" as const,
    textAlign: "left" as const,
    textDecoration: "none",
    fontFamily: theme.typography.calibrateShell.fontFamily,
    fontSize: theme.typography.calibrateShell.fontSize,
    lineHeight: theme.typography.calibrateShell.lineHeight,
    fontWeight: active ? 600 : 500,
    color: nav.iconOnTeal,
    bgcolor: active ? nav.navActive : "transparent",
    boxShadow: active ? nav.navRowShadowActive : "none",
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
    "&::before": active
      ? {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: nav.navActiveAccent,
        }
      : undefined,
    "&:hover": active
      ? undefined
      : { bgcolor: nav.navHover, boxShadow: nav.navRowShadowHover },
    // Inset: a row spans the scroll area, which would clip an outer ring.
    "&.Mui-focusVisible": {
      outline: `2px solid ${nav.navText}`,
      outlineOffset: "-2px",
    },
    [VIEWPORT_2K_MIN_MEDIA]: {
      height: SHELL_NAV_ROW_HEIGHT_2K_PX,
      fontSize: 15,
    },
    [VIEWPORT_4K_MIN_MEDIA]: {
      height: SHELL_NAV_ROW_HEIGHT_4K_PX,
      fontSize: 16,
    },
    [VIEWPORT_SHORT_MAX_MEDIA]: { height: SHELL_NAV_ROW_HEIGHT_SHORT_PX },
    [POINTER_COARSE_MEDIA]: { minHeight: SHELL_NAV_ROW_MIN_TOUCH_PX },
  };
}

const labelSx = {
  color: "calibrate.navText",
  font: "inherit",
  fontWeight: "inherit",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

/** A click the browser should handle itself: a new tab or window. */
function opensElsewhere(event: React.MouseEvent) {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

interface NavRowProps {
  product: NavProduct;
  active: boolean;
  onNavigate: (path: string) => void;
  unreadCount: number;
}

const NavRow = React.forwardRef<HTMLAnchorElement, NavRowProps>(function NavRow(
  { product, active, onNavigate, unreadCount },
  ref,
) {
  const theme = useTheme();
  const icon = PRODUCT_ICONS[product.key](iconSx);
  const unread = product.key === "secureExchange" ? unreadCount : 0;
  return (
    <ButtonBase
      ref={ref}
      href={product.path}
      disableRipple
      aria-current={active ? "page" : undefined}
      aria-label={unread > 0 ? `${product.label}, ${unread} new` : undefined}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        if (event.defaultPrevented || opensElsewhere(event)) return;
        event.preventDefault();
        onNavigate(product.path);
      }}
      sx={rowSx(theme, active)}
    >
      <Box component="span" sx={iconSlotSx}>
        {unread > 0 ? (
          <Badge
            badgeContent={unread}
            color="error"
            overlap="circular"
            slotProps={{ badge: { "aria-hidden": true } }}
          >
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </Box>
      <Typography component="span" sx={labelSx}>
        {product.label}
      </Typography>
    </ButtonBase>
  );
});

interface ShellNavButtonRowProps {
  icon: SvgIconComponent;
  label: string;
  onClick: () => void;
  active?: boolean;
  /** A way out (Logout): red text and a red hover. */
  danger?: boolean;
}

/** A sidebar row that acts instead of leading somewhere (the drawer's footer). */
export function ShellNavButtonRow({
  icon: Icon,
  label,
  onClick,
  active = false,
  danger = false,
}: ShellNavButtonRowProps) {
  const theme = useTheme();
  const base = rowSx(theme, active);
  return (
    <ButtonBase
      disableRipple
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      sx={
        danger
          ? {
              ...base,
              color: "#FFDADA",
              "&:hover": { bgcolor: "rgba(211,47,47,0.35)" },
            }
          : base
      }
    >
      <Box component="span" sx={iconSlotSx}>
        <Icon sx={iconSx} />
      </Box>
      <Typography
        component="span"
        sx={{
          ...labelSx,
          ...(danger ? { color: "#FFDADA", fontWeight: 600 } : undefined),
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

/** The fixed-width column the sidebar sits in from 900 px up; never a rail. */
export function ShellSidebarColumn({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        width: SHELL_SIDEBAR_WIDTH_PX,
        minWidth: SHELL_SIDEBAR_WIDTH_PX,
        maxWidth: SHELL_SIDEBAR_WIDTH_PX,
        [VIEWPORT_2K_MIN_MEDIA]: {
          width: SHELL_SIDEBAR_WIDTH_2K_PX,
          minWidth: SHELL_SIDEBAR_WIDTH_2K_PX,
          maxWidth: SHELL_SIDEBAR_WIDTH_2K_PX,
        },
        [VIEWPORT_4K_MIN_MEDIA]: {
          width: SHELL_SIDEBAR_WIDTH_4K_PX,
          minWidth: SHELL_SIDEBAR_WIDTH_4K_PX,
          maxWidth: SHELL_SIDEBAR_WIDTH_4K_PX,
        },
        flexShrink: 0,
        alignSelf: "stretch",
        bgcolor: "primary.main",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        borderRight: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {children}
    </Box>
  );
}

export interface ShellSidebarProps {
  catalog: NavCatalog;
  pathname: string;
  onNavigate: (path: string) => void;
  /** Secure Exchange files shared with the user and not opened yet. */
  unreadCount?: number;
  ariaLabel: string;
  /** Which sidebar's scroll position to keep across pages. */
  scrollKey: string;
  /** The phone drawer: shows its scroll track on a phone too, and a footer. */
  drawer?: boolean;
  footer?: React.ReactNode;
}

export default function ShellSidebar({
  catalog,
  pathname,
  onNavigate,
  unreadCount = 0,
  ariaLabel,
  scrollKey,
  drawer = false,
  footer,
}: ShellSidebarProps) {
  const theme = useTheme();
  const idBase = useId();
  const activeKey = activeNavKey(catalog, pathname);
  const scrollerRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);
  const activeRowRef = useRef<HTMLAnchorElement | null>(null);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = scrollTops[scrollKey] ?? 0;
  }, [scrollKey]);

  // The active row always shows, nearest edge first, whatever page opened it.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const row = activeRowRef.current;
    if (!scroller || !row) return;
    const box = scroller.getBoundingClientRect();
    const rect = row.getBoundingClientRect();
    if (rect.top < box.top) scroller.scrollTop -= box.top - rect.top;
    else if (rect.bottom > box.bottom)
      scroller.scrollTop += rect.bottom - box.bottom;
  }, [activeKey]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const remember = () => {
      scrollTops[scrollKey] = scroller.scrollTop;
    };
    scroller.addEventListener("scroll", remember, { passive: true });
    return () => scroller.removeEventListener("scroll", remember);
  }, [scrollKey]);

  const row = (product: NavProduct) => (
    <li key={product.key}>
      <NavRow
        ref={product.key === activeKey ? activeRowRef : undefined}
        product={product}
        active={product.key === activeKey}
        onNavigate={onNavigate}
        unreadCount={unreadCount}
      />
    </li>
  );

  return (
    <Box
      component="nav"
      aria-label={ariaLabel}
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "primary.main",
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        <CustomScrollbars
          ref={scrollerRef}
          showOverlayTrack={drawer ? true : undefined}
        >
          <Box
            sx={{
              pt: 2,
              pb: 2,
              [VIEWPORT_SHORT_MAX_MEDIA]: { pt: 1, pb: 1 },
            }}
          >
            <Box component="ul" sx={listSx}>
              {row(catalog.home)}
            </Box>
            {catalog.sections.map((section) => {
              const headerId = `${idBase}-${section.key}`;
              return (
                <Box key={section.key}>
                  <Typography
                    id={headerId}
                    component="div"
                    sx={{
                      px: SHELL_NAV_ROW_INNER_SPACING,
                      pt: 2,
                      pb: 0.5,
                      fontFamily: theme.typography.calibrateShell.fontFamily,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      lineHeight: 1.4,
                      textTransform: "uppercase",
                      color: "calibrate.navSectionHeader",
                      userSelect: "none",
                      [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 12 },
                      [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 13 },
                      [VIEWPORT_SHORT_MAX_MEDIA]: { pt: 1.25, pb: 0.25 },
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box component="ul" aria-labelledby={headerId} sx={listSx}>
                    {section.products.map(row)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CustomScrollbars>
      </Box>
      {footer && (
        <>
          <Divider
            sx={{ borderColor: "rgba(255,255,255,0.2)", my: 1, flexShrink: 0 }}
          />
          <Box sx={{ flexShrink: 0, pb: 1 }}>{footer}</Box>
        </>
      )}
    </Box>
  );
}
